// Boat Icon Components and Configuration
// Shared across LoginPage, WorkoutHistory, and other components

// SVG Boat Icon - handles both sculling (symmetrical) and sweep (staggered) boats
export const BoatIcon = ({ rowers = 1, isSculling = true, showCox = false, className = "" }) => {
  // Sculling = 2 oars per person (symmetrical, both sides at same Y position)
  // Sweep = 1 oar per person (staggered, alternating left-right)
  
  const spacing = rowers <= 2 ? 18 : rowers <= 4 ? 14 : 10;
  const startY = rowers <= 2 ? 35 : rowers <= 4 ? 25 : 20;
  
  return (
    <svg viewBox="0 0 100 120" className={`w-full h-full ${className}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Hull - pointed boat shape */}
      <path
        d="M50 5 L46 25 L48 105 L50 115 L52 105 L54 25 Z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      
      {/* Rower positions and oars */}
      {Array.from({ length: rowers }).map((_, i) => {
        const y = startY + i * spacing;
        
        if (isSculling) {
          // Sculling: symmetrical oars (both sides at same position)
          return (
            <g key={i}>
              {/* Rower marker */}
              <ellipse cx="50" cy={y + 2} rx="4" ry="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
              {/* Left oar */}
              <line x1="46" y1={y} x2="12" y2={y + 4} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              {/* Right oar */}
              <line x1="54" y1={y} x2="88" y2={y + 4} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </g>
          );
        } else {
          // Sweep: staggered oars (alternating left-right)
          const isLeft = i % 2 === 0;
          return (
            <g key={i}>
              {/* Rower marker */}
              <ellipse cx="50" cy={y + 2} rx="4" ry="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
              {/* Single oar - alternating sides */}
              <line 
                x1={isLeft ? 46 : 54} 
                y1={y} 
                x2={isLeft ? 12 : 88} 
                y2={y + 4} 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
              />
            </g>
          );
        }
      })}
      
      {/* Cox position at stern (back of boat) */}
      {showCox && (
        <circle cx="50" cy={startY + rowers * spacing + 8} r="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" />
      )}
    </svg>
  );
};

// Ergometer Icon
export const ErgIcon = ({ className = "" }) => (
  <svg viewBox="0 0 120 80" className={`w-full h-full ${className}`} fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Main beam */}
    <line x1="20" y1="50" x2="100" y2="50" stroke="currentColor" strokeWidth="3" />
    {/* Front support */}
    <path d="M15 70 L25 50 L25 70" stroke="currentColor" strokeWidth="2" fill="none" />
    {/* Flywheel */}
    <circle cx="95" cy="35" r="18" stroke="currentColor" strokeWidth="2" fill="none" />
    <circle cx="95" cy="35" r="12" stroke="currentColor" strokeWidth="1" fill="none" />
    {/* Handle bar */}
    <line x1="95" y1="35" x2="60" y2="35" stroke="currentColor" strokeWidth="2" />
    {/* Seat */}
    <rect x="45" y="42" width="15" height="8" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
    {/* Foot stretcher */}
    <path d="M30 40 L35 55 L40 40" stroke="currentColor" strokeWidth="2" fill="none" />
    {/* Back support */}
    <path d="M100 70 L95 50" stroke="currentColor" strokeWidth="2" />
    <path d="M105 70 L95 35" stroke="currentColor" strokeWidth="2" />
  </svg>
);

// Full boat class list based on rowing conventions
export const BOAT_TYPES = [
  { id: '1x', name: '1x', fullName: 'Single Scull', rowers: 1, isSculling: true },
  { id: '2-', name: '2-', fullName: 'Coxless Pair', rowers: 2, isSculling: false },
  { id: '2x', name: '2x', fullName: 'Double Scull', rowers: 2, isSculling: true },
  { id: '4-', name: '4-', fullName: 'Coxless Four', rowers: 4, isSculling: false },
  { id: '4+', name: '4+', fullName: 'Coxed Four', rowers: 4, isSculling: false, showCox: true },
  { id: '4x', name: '4x', fullName: 'Quad Scull', rowers: 4, isSculling: true },
  { id: '4x+', name: '4x+', fullName: 'Coxed Quad', rowers: 4, isSculling: true, showCox: true },
  { id: '8+', name: '8+', fullName: 'Eight', rowers: 8, isSculling: false, showCox: true },
  { id: '8x+', name: '8x+', fullName: 'Octuple', rowers: 8, isSculling: true, showCox: true },
  { id: 'c2_erg', name: 'ERG', fullName: 'Ergometer', isErg: true },
];

// Helper function to get boat configuration by ID
export const getBoatById = (boatId) => {
  return BOAT_TYPES.find(boat => boat.id === boatId) || null;
};

// Compact Boat Icon Component - renders the appropriate icon based on boat type
export const BoatTypeIcon = ({ boatType, size = 'md', className = "" }) => {
  const boat = getBoatById(boatType);
  
  if (!boat) return null;
  
  // Size classes
  const sizeClasses = {
    sm: 'w-6 h-8',
    md: 'w-8 h-10',
    lg: 'w-12 h-16',
    xl: 'w-16 h-20'
  };
  
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  
  return (
    <div className={`${sizeClass} ${className}`} title={boat.fullName}>
      {boat.isErg ? (
        <ErgIcon />
      ) : (
        <BoatIcon rowers={boat.rowers} isSculling={boat.isSculling} showCox={boat.showCox} />
      )}
    </div>
  );
};

export default { BoatIcon, ErgIcon, BOAT_TYPES, getBoatById, BoatTypeIcon };
